'use client';

import { useEffect } from 'react';

/**
 * Analytics Component
 * Optimized for performance: Delays the injection of heavy tracking scripts
 * until the page is interactive to free up the main thread.
 * 
 * FIX: Hardened global error filtering to suppress "Failed to fetch" errors
 * caused by ad-blockers (like Ghostery) blocking analytics scripts.
 */
export default function Analytics() {
  useEffect(() => {
    // SILENCE AD-BLOCKER ERRORS:
    // Extensions like Ghostery wrap window.fetch and throw when blocking.
    // We catch these globally during the capture phase to prevent them from 
    // triggering the Next.js development overlay.
    const handleSafeError = (event: ErrorEvent | PromiseRejectionEvent) => {
      const error = 'error' in event ? event.error : (event as PromiseRejectionEvent).reason;
      const message = error?.message || (event as ErrorEvent).message || '';
      
      // Specifically target network errors commonly thrown by intercepted tracking calls
      const isExtensionError = 
        message.includes('Failed to fetch') || 
        message.includes('NetworkError') ||
        message.includes('extension') ||
        message.includes('Script error') ||
        message.includes('GTM') ||
        message.includes('clarity');

      if (isExtensionError) {
        // Prevent the error from bubbling up to the Next.js overlay
        if (event.preventDefault) event.preventDefault();
        if (event.stopPropagation) event.stopPropagation();
        return true;
      }
    };

    // Use capture phase (true) to intercept errors before they reach other handlers
    window.addEventListener('error', handleSafeError, true);
    window.addEventListener('unhandledrejection', handleSafeError, true);

    const timer = setTimeout(() => {
      try {
        // 1. Google Tag Manager
        if (!document.getElementById("gtm-delayed")) {
          const gtmScript = document.createElement("script");
          gtmScript.id = "gtm-delayed";
          gtmScript.innerHTML = `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-KFQZTGDP');
          `;
          document.head.appendChild(gtmScript);
        }

        // 2. Microsoft Clarity
        if (!document.getElementById("clarity-delayed")) {
          const clarityScript = document.createElement("script");
          clarityScript.id = "clarity-delayed";
          clarityScript.innerHTML = `
            (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i+"?ref=bwt";
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "vbmx3mul8g");
          `;
          document.head.appendChild(clarityScript);
        }
      } catch (e) {
        // Silently fail if injection is blocked or fails
        console.warn("Analytics injection blocked by browser/extension.");
      }
    }, 3000); // 3-second delay for peak performance

    return () => {
      clearTimeout(timer);
      window.removeEventListener('error', handleSafeError, true);
      window.removeEventListener('unhandledrejection', handleSafeError, true);
    };
  }, []);

  return null;
}
